import { useState, useRef, useEffect } from 'react';
import { Monitor, ChevronDown, Wifi, WifiOff, Cloud, CloudOff, ScreenShare } from 'lucide-react';
import { useMachineStore } from '../stores/machineStore';
import { useAuthStore } from '../stores/authStore';
import { useProjectStore } from '../stores/projectStore';
import { useRdpStore } from '../stores/rdpStore';
import { peerConnection } from '../services/peerConnection';
import { switchToWebRTCBackend, switchToCefBackend, isCefHost } from '../services/backend';
import { logger } from '@/lib/logger';
import { RDP_ENABLED } from '../config/features';

interface MachineSelectorProps {
  isCollapsed: boolean;
}

export default function MachineSelector({ isCollapsed }: MachineSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const machines = useMachineStore((state) => state.machines);
  const selectedMachineId = useMachineStore((state) => state.selectedMachineId);
  const localMachineId = useMachineStore((state) => state.localMachineId);
  const selectMachine = useMachineStore((state) => state.selectMachine);

  const { isAuthenticated } = useAuthStore();
  const loadProjects = useProjectStore((state) => state.loadProjects);
  // Local desktop (CEF host) means we're NOT in web mode
  const localDesktop = isCefHost();
  const webMode = !localDesktop;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get the store functions for auto-reconnect
  const setLastConnectedMachine = useMachineStore((state) => state.setLastConnectedMachine);

  // Listen for WebRTC disconnect events — save for auto-reconnect but keep selection
  useEffect(() => {
    const unsubscribe = peerConnection.onDisconnect(() => {
      logger.debug('MachineSelector', 'WebRTC disconnected, keeping selection for reconnect');
      // Save the current machine ID for auto-reconnect but DON'T clear selectedMachineId
      // so the UI stays functional and the machine shows as selected (just offline)
      if (selectedMachineId && selectedMachineId !== localMachineId) {
        setLastConnectedMachine(selectedMachineId);
      }
    });

    return () => unsubscribe();
  }, [selectedMachineId, localMachineId, setLastConnectedMachine]);

  // In Tauri mode, always show (local machine is always available)
  // In web mode, always show (to prompt sign in or show machines)

  const selectedMachine = machines.find((m) => m.id === selectedMachineId);
  // In desktop mode: local is selected when no machine ID or matches local machine
  // In web mode: nothing is "local", user must select a remote machine
  const isLocalSelected = !webMode && (!selectedMachineId || selectedMachineId === localMachineId);
  const hasRemoteSelected = selectedMachine && selectedMachineId !== localMachineId;

  const handleSelectMachine = async (machineId: string | null) => {
    selectMachine(machineId);
    setIsOpen(false);

    // If selecting a remote machine, initiate WebRTC connection
    if (machineId && machineId !== localMachineId) {
      try {
        // Check if already connected to this machine (connect() returns false if this.pc exists)
        if (peerConnection.isConnected) {
          logger.debug('MachineSelector', 'Already connected via WebRTC, ensuring state is consistent');
          switchToWebRTCBackend();
          useMachineStore.getState().setWebrtcReady(true);
          await loadProjects();
          return;
        }

        logger.debug('MachineSelector', 'Connecting to remote machine:', machineId);
        useMachineStore.getState().setWebrtcConnecting(true);
        const connected = await peerConnection.connect(machineId);

        if (connected) {
          logger.debug('MachineSelector', 'WebRTC connected, switching backend');
          switchToWebRTCBackend();
          useMachineStore.getState().setWebrtcReady(true);
          // Reload projects from the remote machine
          await loadProjects();
        } else {
          logger.warn('MachineSelector', 'Failed to connect to remote machine');
          useMachineStore.getState().setLastConnectedMachine(machineId);
        }
      } catch (error) {
        logger.error('MachineSelector', 'Connection error:', error);
      } finally {
        useMachineStore.getState().setWebrtcConnecting(false);
      }
    } else if (!webMode && (!machineId || machineId === localMachineId)) {
      // Switching back to local machine (only in desktop mode)
      if (isCefHost()) {
        logger.debug('MachineSelector', 'Switching to local CefBackend');
        switchToCefBackend();
        await loadProjects();
      }
    }
  };

  // Handle RDP button click — select the machine, connect, and flag for RDP tab
  const handleRdp = async (machineId: string) => {
    setIsOpen(false);
    // First, select the machine and connect
    await handleSelectMachine(machineId);
    // Set a flag in the RDP store that signals the workspace to open the RDP viewer
    useRdpStore.getState().setViewing(true, machineId);
  };

  // Platform icon based on OS
  const getPlatformIcon = (platform: 'windows' | 'macos' | 'linux') => {
    switch (platform) {
      case 'windows':
        return '🪟';
      case 'macos':
        return '🍎';
      case 'linux':
        return '🐧';
      default:
        return '💻';
    }
  };

  if (isCollapsed) {
    // Collapsed state - just show icon with connection indicator
    return (
      <div className="p-2">
        <div
          className="relative flex items-center justify-center p-2 rounded-lg bg-base-300 cursor-pointer hover:bg-base-300/80"
          onClick={() => setIsOpen(!isOpen)}
          title={isLocalSelected ? 'Local Machine' : selectedMachine?.name || 'Select Machine'}
        >
          <Monitor className="w-5 h-5" />
          {/* Connection status dot */}
          <span
            className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${
              isLocalSelected ? 'bg-success' : selectedMachine?.isOnline ? 'bg-info' : 'bg-warning'
            }`}
          />
        </div>

        {/* Collapsed dropdown */}
        {isOpen && (
          <div
            ref={dropdownRef}
            className="absolute left-20 top-2 z-50 bg-base-200 border border-base-300 rounded-lg shadow-xl min-w-64"
          >
            <MachineList
              machines={machines}
              localMachineId={localMachineId}
              selectedMachineId={selectedMachineId}
              onSelect={handleSelectMachine}
              onRdp={webMode ? handleRdp : undefined}
              getPlatformIcon={getPlatformIcon}
            />
          </div>
        )}
      </div>
    );
  }

  // Expanded state - full dropdown
  return (
    <div className="p-2" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-base-300 hover:bg-base-300/80 transition-colors"
      >
        {/* Machine Icon */}
        <div className="relative">
          {isLocalSelected ? (
            <Monitor className="w-5 h-5" />
          ) : hasRemoteSelected ? (
            <Cloud className="w-5 h-5 text-info" />
          ) : (
            <Cloud className="w-5 h-5 text-base-content/40" />
          )}
        </div>

        {/* Machine Name */}
        <div className="flex-1 text-left min-w-0">
          <div className="text-sm font-medium truncate">
            {isLocalSelected
              ? 'This Machine'
              : hasRemoteSelected
                ? selectedMachine?.name
                : 'Select Machine'}
          </div>
          <div className="text-xs text-base-content/60 truncate">
            {isLocalSelected ? (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-success inline-block" />
                Local
              </span>
            ) : hasRemoteSelected ? (
              <span className="flex items-center gap-1">
                {selectedMachine?.isOnline ? (
                  <>
                    <Wifi className="w-3 h-3 text-success" />
                    Online
                  </>
                ) : (
                  <>
                    <WifiOff className="w-3 h-3 text-warning" />
                    Offline
                  </>
                )}
              </span>
            ) : (
              <span className="text-base-content/40">
                Choose a machine to connect
              </span>
            )}
          </div>
        </div>

        {/* Dropdown Arrow */}
        <ChevronDown
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute left-2 right-2 mt-1 z-50 bg-base-200 border border-base-300 rounded-lg shadow-xl">
          <MachineList
            machines={machines}
            localMachineId={localMachineId}
            selectedMachineId={selectedMachineId}
            onSelect={handleSelectMachine}
            onRdp={webMode ? handleRdp : undefined}
            getPlatformIcon={getPlatformIcon}
            webMode={webMode}
            isAuthenticated={isAuthenticated}
          />
        </div>
      )}
    </div>
  );
}

// Reusable machine list component
interface MachineListProps {
  machines: Array<{
    id: string;
    name: string;
    platform: 'windows' | 'macos' | 'linux';
    isOnline: boolean;
    isOwn: boolean;
  }>;
  localMachineId: string | null;
  selectedMachineId: string | null;
  onSelect: (machineId: string | null) => void;
  onRdp?: (machineId: string) => void;
  getPlatformIcon: (platform: 'windows' | 'macos' | 'linux') => string;
  webMode?: boolean;
  isAuthenticated?: boolean;
}

function MachineList({
  machines,
  localMachineId,
  selectedMachineId,
  onSelect,
  onRdp,
  getPlatformIcon,
  webMode = false,
  isAuthenticated = false,
}: MachineListProps) {
  const isLocalSelected = !selectedMachineId || selectedMachineId === localMachineId;
  // Filter out local machine from remote list - but only if localMachineId is set
  const remoteMachines = localMachineId
    ? machines.filter((m) => m.id !== localMachineId)
    : machines;

  // In web mode and not authenticated, show sign in prompt
  if (webMode && !isAuthenticated) {
    return (
      <div className="py-1">
        <div className="px-3 py-4 text-center text-sm text-base-content/60">
          <Cloud className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>Sign in to see your machines</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-1">
      {/* Header */}
      <div className="px-3 py-2 text-xs font-semibold text-base-content/60 uppercase tracking-wider">
        {webMode ? 'Select Machine' : 'My Machines'}
      </div>

      {/* Local Machine Option - only show in Tauri mode */}
      {!webMode && (
        <button
          onClick={() => onSelect(null)}
          className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-base-300 transition-colors ${
            isLocalSelected ? 'bg-base-300' : ''
          }`}
        >
          <Monitor className="w-5 h-5" />
          <div className="flex-1 text-left">
            <div className="text-sm font-medium">This Machine</div>
            <div className="text-xs text-base-content/60">Local</div>
          </div>
          <span className="w-2 h-2 rounded-full bg-success" />
        </button>
      )}

      {/* Divider */}
      {!webMode && remoteMachines.length > 0 && <div className="divider my-1 px-3" />}

      {/* Remote Machines */}
      {remoteMachines.map((machine) => (
          <div
            key={machine.id}
            className={`flex items-center gap-3 px-3 py-2 hover:bg-base-300 transition-colors ${
              selectedMachineId === machine.id ? 'bg-base-300' : ''
            } ${!machine.isOnline ? 'opacity-50' : ''}`}
          >
            <button
              onClick={() => onSelect(machine.id)}
              disabled={!machine.isOnline}
              className="flex items-center gap-3 flex-1 min-w-0 text-left disabled:cursor-not-allowed"
            >
              {/* Platform Icon */}
              <span className="text-lg">{getPlatformIcon(machine.platform)}</span>

              {/* Machine Info */}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{machine.name}</div>
                <div className="text-xs text-base-content/60 flex items-center gap-1">
                  {machine.isOnline ? (
                    <>
                      <Wifi className="w-3 h-3 text-success" />
                      Online
                    </>
                  ) : (
                    <>
                      <CloudOff className="w-3 h-3 text-warning" />
                      Offline
                    </>
                  )}
                </div>
              </div>

              {/* Selection indicator */}
              {selectedMachineId === machine.id && (
                <span className="w-2 h-2 rounded-full bg-info" />
              )}
            </button>

            {/* RDP button — hidden until Remote Desktop is ready to ship */}
            {RDP_ENABLED && machine.isOnline && onRdp && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRdp(machine.id);
                }}
                className="btn btn-ghost btn-xs btn-square flex-shrink-0"
                title="Remote Desktop"
              >
                <ScreenShare className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}

      {/* Empty State */}
      {remoteMachines.length === 0 && (
        <div className="px-3 py-4 text-center text-sm text-base-content/60">
          <Cloud className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>{webMode ? 'No machines online' : 'No other machines registered'}</p>
          <p className="text-xs mt-1">
            {webMode
              ? 'Start AtelierCode on a machine to connect'
              : 'Run AtelierCode on another device to connect'}
          </p>
        </div>
      )}
    </div>
  );
}
